<template>
  <div class="d-flex date-range-picker-row">
    <!-- Right form -->
    <div
      class="date-range-picker-col"
    >
      <div
        v-if="setRangeSelectLabel"
        class="set-range-select-title font-family-notosanscjkkr-medium">{{ setRangeSelectLabel }}</div>
      <div class="form-group custom-select-time-frame">
        <select
          :class="compare ? 'date-range-picker-range-border' : ''"
          v-model="rangeSelect"
          class="custom-select"
          name="range_select">
          <option
            v-for="(range, rangeKey) in ranges"
            :key="rangeKey"
            :value="rangeKey">{{ $t(range.label) }}</option>
          <option value="custom">{{ $t('costAnalytics.header.timeFrameOptions.custom') }}</option>
        </select>
      </div>
      <div
        :style="{ paddingBottom : isValidDuration && isSingleDurationValid && isStartDateLessThanOrEqualToEndDate ? '10px' : '0'}"
        class="form-group form-inline flex-nowrap input-date-time"
      >
        <input
          ref="startDate"
          :disabled="rangeSelect !== 'custom'"
          :value="dateFormat(startDate,'startDate')"
          :class="{'invalid-date-border': !isSingleDurationValid || !isValidDuration}"
          type="text"
          class="form-control date-range-picker-date-input"
          @keypress="isNumber($event)"
          @focus="step = 'selectStartDate'"
          @blur="inputDate"
          @change="onChangeDate"
        >
        <span class="margin-dash">
          -
        </span>
        <input
          ref="endDate"
          :disabled="rangeSelect !== 'custom'"
          :value="dateFormat(endDate, 'endDate')"
          :class="{'invalid-date-border': !isSingleDurationValid || !isStartDateLessThanOrEqualToEndDate}"
          type="text"
          class="form-control date-range-picker-date-input"
          @keypress="isNumber($event)"
          @focus="step = 'selectEndDate'"
          @blur="inputDate"
          @change="onChangeDate"
        >
      </div>
      <div
        v-if="!isValidDuration"
        :class="isValidDuration ? '' : 'invalid-date-text'"
      >
        {{ message.single.exceeded12Months }}
      </div>
      <div
        v-if="!isSingleDurationValid"
        :class="isSingleDurationValid ? '' : 'invalid-date-text'"
      >
        {{ message.single.exceeded93Days }}
      </div>
      <div
        v-if="!isStartDateLessThanOrEqualToEndDate"
        :class="isStartDateLessThanOrEqualToEndDate ? '' : 'invalid-date-text'"
      >
        {{ message.single.endDateIsLessThanStartDate }}
      </div>
      <fragment v-if="allowCompare">
        <div class="form-group">
          <div class="custom-control custom-checkbox">
            <input
              :id="'date-range-picker-compare-' + _uid"
              v-model="compare"
              type="checkbox"
              class="custom-control-input">
            <label
              :for="'date-range-picker-compare-' + _uid"
              class="custom-control-label font-family-notosanscjkkr-medium">{{ compareLabel }}</label>
          </div>
        </div>
        <div>
          <div class="form-group custom-select-time-frame">
            <select
              :class="compare ? 'date-range-picker-range-border compare text-darkgray-1' : ''"
              v-model="rangeSelectCompare"
              :disabled="!compare"
              class="custom-select"
              name="range_select_compare"
              @change="onRangeSelectCompareChanged">
              <option
                v-for="(range, rangeKey) in compareRanges"
                :key="rangeKey"
                :value="rangeKey">{{ range.label }}</option>
              <option value="last_period">{{ $t('costAnalytics.header.compareOptions.lastPeriod') }}</option>
              <option value="custom">{{ $t('costAnalytics.header.compareOptions.custom') }}</option>
            </select>
          </div>
          <div class="form-group form-inline flex-nowrap input-compare-date-time">
            <input
              ref="startDateCompare"
              :disabled="(rangeSelectCompare !== 'custom' || !compare)"
              :value="compare ? dateFormat(startDateCompare, 'startDateCompare') : ''"
              :class="isCompareDurationValid ? '' : 'invalid-date-border'"
              type="text"
              class="form-control w-100 date-range-picker-date-input compare"
              @keypress="isNumber($event)"
              @focus="step = 'selectStartDateCompare'"
              @blur="inputCompareDate"
              @change="onChangeCompareDate"
            >
            <span class="margin-dash">
              -
            </span>
            <input
              ref="endDateCompare"
              :value="compare ? dateFormat(endDateCompare, 'endDateCompare') : ''"
              :disabled="(rangeSelectCompare !== 'custom' || !compare)"
              :class="{'invalid-date-border': !isCompareStartDateLessThanOrEqualToCompareEndDate || !isCompareDurationValid}"
              type="text"
              class="form-control w-100 date-range-picker-date-input compare"
              @keypress="isNumber($event)"
              @focus="step = 'selectEndDateCompare'"
              @blur="inputCompareDate"
              @change="onChangeCompareDate"
            >
          </div>
          <div
            v-if="!isCompareDurationValid"
            :class="isCompareDurationValid ? '' : 'invalid-date-text'"
          >
            {{ message.compare.exceeded93Days }}
          </div>
          <div
            v-if="!isCompareStartDateLessThanOrEqualToCompareEndDate"
            :class="isCompareStartDateLessThanOrEqualToCompareEndDate ? '' : 'invalid-date-text'"
          >
            {{ message.compare.endDateIsLessThanStartDate }}
          </div>
          <!--          <div-->
          <!--            v-if="compareCostTypeOptions.length > 0"-->
          <!--            class="form-group">-->
          <!--            <b-form-radio-group-->
          <!--              v-model="compareCostType"-->
          <!--              :options="compareCostTypeOptions"-->
          <!--              :disabled="!compare"-->
          <!--              class="mb-1"-->
          <!--              stacked-->
          <!--              @change="changeCompareCostType"-->
          <!--            />-->
          <!--          </div>-->
        </div>
      </fragment>
    </div>

    <!-- Calendars -->
    <div
      v-for="calendarIndex in calendarCount"
      :key="calendarIndex"
      class="date-range-picker-calendar-col">
      <date-range-picker-calendar
        :calendar-index="calendarIndex"
        :calendar-count="calendarCount"
        :month="month"
        :start-date="startDate"
        :end-date="endDate"
        :compare="compare"
        :start-date-compare="startDateCompare"
        :end-date-compare="endDateCompare"
        :step="step"
        :previous-step="previousStep"
        :range-select-compare="rangeSelectCompare"
        :range-select="rangeSelect"
        :is-before-limit="isBeforeLimit"
        @goToPrevMonth="goToPrevMonth"
        @goToNextMonth="goToNextMonth"
        @selectDate="selectDate"
        @nextStep="nextStep"
      />
    </div>
    <div class="form-group form-inline justify-content-end mb-0 custom-btn-group">
      <button
        type="button"
        class="btn btn-light font-family-notosanscjkkr-medium"
        @click="cancel">{{ cancelLabel }}</button>
      <button
        :disabled="!isSubmitButtonEnabled"
        type="button"
        class="btn btn-primary ml-2 font-family-notosanscjkkr-medium"
        @click="submit">{{ submitLabel }}</button>
    </div>
  </div>
</template>

<script>
import DateRangePickerCalendar from './DateRangePickerCalendar'
import _isEmpty from 'lodash/isEmpty';
import {getFullDateFormatByLocalization} from "@/util/dateTimeUtils";
import {COST_ANALYTICS_TIME_FRAME} from "@/constants/costAnalyticsConstants";

const MAX_DATE_93_DAY = 93;

const STEP = {
  SELECT_START_DATE: 'selectStartDate',
  SELECT_END_DATE: 'selectEndDate',
  SELECT_START_DATE_COMPARE: 'selectStartDateCompare',
  SELECT_END_DATE_COMPARE: 'selectEndDateCompare',
  START_DATE_COMPARE: 'startDateCompare'
}

const RANGE_SELECT = {
  CUSTOM: 'custom',
  LAST_PERIOD: 'last_period'
}

const DATE_FORMAT = {
  YYYYMMDD: 'YYYY/MM/DD',
  MMDDYYYY: 'MM/DD/YYYY',
}

export default {
  name: 'DateRangePicker',
  components: { DateRangePickerCalendar },
  props: {
    calendarCount: {
      type: Number,
      default: 2
    },
    cancelLabel: {
      type: String,
      default: 'Cancel'
    },
    submitLabel: {
      type: String,
      default: 'Cancel'
    },
    compareLabel: {
      type: String,
      default: 'Compare'
    },
    setRangeSelectLabel: {
      type: String,
      default: ''
    },
    allowCompare: {
      type: Boolean,
      default: true
    },
    compareCostTypeOptions: {
      type: Array,
      default: function() {
        return []
      }
    },
    defaultCompareCostType: {
      type: String,
      default: ''
    },
    ranges: {
      type: Object,
      default: function() {
        return {
          currentMonth: {
            label: 'Current month',
            startDate: this.$dayjs.utc().startOf('month'),
            endDate: this.$dayjs.utc().endOf('month').startOf('day')
          },
          lastMonth: {
            label: 'Last month',
            startDate: this.$dayjs.utc().subtract(1, 'month').startOf('month'),
            endDate: this.$dayjs.utc().subtract(1, 'month').endOf('month').startOf('day')
          }
        }
      }
    },
    compareRanges: {
      type: Object,
      default: function() {}
    },
    defaultRangeSelect: {
      type: String,
      default: 'currentMonth'
    },
    defaultRangeSelectCompare: {
      type: String,
      default: RANGE_SELECT.LAST_PERIOD
    },
    defaultStartDate: {
      type: [Object, String],
      default: null
    },
    defaultEndDate: {
      type: [Object, String],
      default: null
    },
    defaultStartDateCompare: {
      type: [Object, String],
      default: null
    },
    defaultEndDateCompare: {
      type: [Object, String],
      default: null
    },
    defaultCompare: {
      type: Boolean,
      default: false
    },
    filterSettings: {
      type: Object,
      default() {
        return {}
      }
    },
    /**
     * 이전 날짜 선택 제약 여부
     */
    isBeforeLimit: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      startDate: this.$dayjs.utc(),
      endDate: this.$dayjs.utc(),
      startDateCompare: '',
      endDateCompare: '',
      rangeSelect: null,
      rangeSelectCompare: this.defaultRangeSelectCompare,
      compareCostType: null,
      compare: false,
      month: this.$dayjs.utc().subtract(1, 'month').startOf('month'),
      step: null,
      previousStep: null,
      warningModal: 'warning-modal',
      message: {
        single: {
          exceeded12Months: '',
          endDateIsLessThanStartDate: '',
          exceeded93Days: ''
        },
        compare: {
          exceeded93Days: '',
          endDateIsLessThanStartDate: ''
        }
      },
    }
  },
  computed: {
    isSubmitButtonEnabled() {
      return this.isStartDateLessThanOrEqualToEndDate
       && this.isValidDuration
       && this.isCompareStartDateLessThanOrEqualToCompareEndDate
       && this.isCompareDurationValid
       && this.isSingleDurationValid
    },
    isCompareDurationValid() {
      return _isEmpty(this.message.compare.exceeded93Days)
    },
    isCompareStartDateLessThanOrEqualToCompareEndDate() {
      return _isEmpty(this.message.compare.endDateIsLessThanStartDate)
    },
    isSingleDurationValid() {
      return _isEmpty(this.message.single.exceeded93Days)
    },
    isValidDuration() {
      return _isEmpty(this.message.single.exceeded12Months)
    },
    isStartDateLessThanOrEqualToEndDate() {
      return _isEmpty(this.message.single.endDateIsLessThanStartDate)
    }
  },
  watch: {
    rangeSelect: function() {
      this.selectRange(this.rangeSelect)
      this.message.single.exceeded93Days = '';
      this.message.compare.exceeded93Days = '';
      if (this.rangeSelect !== RANGE_SELECT.CUSTOM) {
        this.message.single.exceeded12Months = '';
        this.message.single.endDateIsLessThanStartDate = '';
        if (this.rangeSelectCompare !== RANGE_SELECT.CUSTOM) {
          this.getStartDateAndEndDateCompareRangeLastPeriod();
        } else {
          this.getStartDateAndEndDateCompareRangeCustom();
        }
      } else {
        // 사용자지정(custom) 선택 시 startDate input 창으로 focus
        this.$refs.startDate.focus();

        if (this.rangeSelectCompare === RANGE_SELECT.LAST_PERIOD) {
          this.getStartDateAndEndDateCompareRangeLastPeriod();
        } else {
          this.getStartDateAndEndDateCompareRangeCustom();
        }
      }
    },
    rangeSelectCompare: function() {
      this.selectRangeCompare(this.rangeSelectCompare);
      this.message.single.exceeded93Days = '';
      if (this.rangeSelectCompare !== RANGE_SELECT.CUSTOM) {
        this.message.compare.exceeded93Days = '';
        this.message.compare.endDateIsLessThanStartDate = '';
        this.getStartDateAndEndDateCompareRangeLastPeriod();
      } else {
        this.getStartDateAndEndDateCompareRangeCustom();
      }
    },
    endDate: {
      handler() {
        if (this.step === STEP.SELECT_END_DATE) {
          this.validateSelectCalendar()
          if (this.rangeSelectCompare === RANGE_SELECT.LAST_PERIOD) {
            this.getStartDateAndEndDateCompareRangeLastPeriod();
          } else {
            this.getStartDateAndEndDateCompareRangeCustom();
          }
        }
      },
      immediate: true
    },
    endDateCompare: {
      handler() {
        if (this.step === STEP.SELECT_END_DATE_COMPARE) {
          this.validateSelectCompareCalendar();
        }
      },
      immediate: true
    },
    compare: {
      handler() {
        if (!this.compare) {
          this.message.single.exceeded93Days = '';
          this.message.compare.exceeded93Days = '';
          this.message.compare.endDateIsLessThanStartDate = '';
          this.rangeSelectCompare = RANGE_SELECT.LAST_PERIOD
        }
        this.validateCustomTimeFrame()
      },
      immediate: true
    }
  },
  created: function() {
    // Initialize ranges
    this.rangeSelect = this.defaultRangeSelect
    this.rangeSelectCompare = this.defaultRangeSelectCompare
    this.compare = this.defaultCompare
    if (this.rangeSelect === RANGE_SELECT.CUSTOM) {
      this.startDate = this.$dayjs.utc().startOf('month')
      if (this.defaultStartDate) {
        this.startDate = this.$dayjs.utc(this.defaultStartDate, DATE_FORMAT.YYYYMMDD)
      }
      this.endDate = this.$dayjs.utc().endOf('month').startOf('day')
      if (this.defaultEndDate) {
        this.endDate = this.$dayjs.utc(this.defaultEndDate, DATE_FORMAT.YYYYMMDD)
      }
    }
    if (this.defaultCompareCostType) {
      this.compareCostType = this.defaultCompareCostType
    }
  },
  mounted() {
    this.refreshViewpointMonth();

    setTimeout(() => {
      this.$refs.startDate.blur()
      this.$refs.endDate.blur()
      this.$refs.startDateCompare.blur()
      this.$refs.endDateCompare.blur()
    }, 10)
    if (this.step === null && this.rangeSelect === RANGE_SELECT.CUSTOM && this.previousStep === STEP.SELECT_END_DATE) {
      this.step = STEP.SELECT_END_DATE;
      this.$refs.endDate.focus()
    }
  },
  methods: {
    getStartDateAndEndDateCompareRangeCustom() {
      if (this.rangeSelectCompare === RANGE_SELECT.CUSTOM) {
        this.getStartDateAndEndDateCompareRangeLastPeriod();
        if (this.defaultStartDateCompare) {
          this.startDateCompare = this.$dayjs.utc(this.defaultStartDateCompare, DATE_FORMAT.YYYYMMDD);
        }
        if (this.defaultEndDateCompare) {
          this.endDateCompare = this.$dayjs.utc(this.defaultEndDateCompare, DATE_FORMAT.YYYYMMDD);
        }
      }
    },
    getStartDateAndEndDateCompareRangeLastPeriod() {
      switch (this.rangeSelect) {
        case COST_ANALYTICS_TIME_FRAME.MONTH_TO_DATE: {
          this.startDateCompare = this.$dayjs.utc().subtract(1, 'month').startOf('month');
          this.endDateCompare = this.$dayjs.utc().subtract(1, 'month');
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.LAST_MONTH: {
          this.startDateCompare = this.$dayjs.utc().subtract(2, 'month').startOf('month');
          this.endDateCompare = this.$dayjs.utc().subtract(2, 'month').endOf('month');
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.LAST_7_DAYS: {
          this.startDateCompare = this.$dayjs.utc().subtract(2 * 7 - 1, 'day');
          this.endDateCompare = this.$dayjs.utc().subtract(7, 'day');
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.LAST_14_DAYS: {
          this.startDateCompare = this.$dayjs.utc().subtract(2 * 14 - 1, 'day');
          this.endDateCompare = this.$dayjs.utc().subtract(14, 'day');
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.LAST_60_DAYS: {
          this.startDateCompare = this.$dayjs.utc().subtract(2 * 60 - 1, 'day');
          this.endDateCompare = this.$dayjs.utc().subtract(60, 'day');
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.LAST_3_MONTHS: {
          this.startDateCompare = this.$dayjs.utc().subtract(3 * 2 - 1, 'month').startOf('month');
          this.endDateCompare = this.$dayjs.utc().subtract(3, 'month').endOf('month');
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.LAST_6_MONTHS: {
          this.startDateCompare = this.$dayjs.utc().subtract(MAX_DATE_93_DAY * 2 + 1, 'day');
          this.endDateCompare = this.$dayjs.utc().subtract(MAX_DATE_93_DAY + 1, 'day');
          this.step = STEP.START_DATE_COMPARE;
          this.previousStep = STEP.START_DATE_COMPARE;
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.LAST_12_MONTHS: {
          this.startDateCompare = this.$dayjs.utc().subtract(MAX_DATE_93_DAY * 2 + 1, 'day');
          this.endDateCompare = this.$dayjs.utc().subtract(MAX_DATE_93_DAY + 1, 'day');
          this.step = STEP.START_DATE_COMPARE;
          this.previousStep = STEP.START_DATE_COMPARE;
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.YEAR_TO_DATE: {
          let firstDayOfYear = this.$dayjs.utc(this.endDate).month(0).date(1)
          let compareDays = (this.$dayjs.utc(this.endDate).diff(firstDayOfYear, 'day'));
          if (compareDays > 93) {
            compareDays = 93
          }
          this.endDateCompare = this.$dayjs.utc(this.endDate).subtract(1, 'year');
          this.startDateCompare = this.endDateCompare.subtract(compareDays, 'day');
          break;
        }
        case COST_ANALYTICS_TIME_FRAME.CUSTOM: {
          let compareDays = (this.$dayjs.utc(this.endDate).diff(this.startDate, 'day'));
          if (compareDays > 93) {
            compareDays = 93
          }
          this.endDateCompare = this.$dayjs.utc(this.startDate).subtract(1, 'day');
          this.startDateCompare = this.endDateCompare.subtract(compareDays, 'day');
          break;
        }
      }
    },
    dateFormat: function(value, type) {
      return value ? this.$dayjs.utc(value).format(getFullDateFormatByLocalization()) : ''
    },
    refreshViewpointMonth() {
      const durationMonth = this.$dayjs.utc().diff(this.startDate, 'month')
      this.month = this.$dayjs.utc().subtract(durationMonth + 1, 'month').startOf('month')
    },
    goToPrevMonth: function() {
      this.month = this.$dayjs.utc(this.month).subtract(1, 'month')
    },
    goToNextMonth: function() {
      this.month = this.$dayjs.utc(this.month).add(1, 'month')
    },
    selectRange: function(rangeKey) {
      let predefinedRange = false

      // Predefined ranges
      for (const _rangeKey of Object.keys(this.ranges)) {
        const range = this.ranges[_rangeKey]
        if (rangeKey === _rangeKey) {
          predefinedRange = true

          if (!this.startDate.isSame(range.startDate)) {
            this.startDate = this.$dayjs.utc(range.startDate)
          }
          if (!this.endDate.isSame(range.endDate)) {
            this.endDate = this.$dayjs.utc(range.endDate)
          }
        }
      }
      if (this.compare) {
        this.validateCustomTimeFrame();
      }
      // Custom range
      if (!predefinedRange && this.step == null) {
        this.step = STEP.SELECT_START_DATE;
        this.previousStep = STEP.SELECT_START_DATE;
        const $vm = this
        setTimeout(function() {
          $vm.$refs.startDate && $vm.$refs.startDate.focus()
        }, 0)
      } else if (this.step !== null) {
        //Fix bug change range select after save custom range (focus input date)
        this.step = null;
      }
      this.refreshViewpointMonth()
    },
    changeCompareCostType: function(checked) {
      this.compareCostType = checked
    },
    onRangeSelectCompareChanged: function(event) {
      const rangeKey = event.target.value
      this.selectRangeCompare(rangeKey)
    },
    selectRangeCompare: function(rangeKey) {
      let predefinedRange = false

      // Predefined compareRanges ranges
      if (this.compareRanges) {
        for (const _rangeKey of Object.keys(this.compareRanges)) {
          const range = this.compareRanges[_rangeKey]
          if (rangeKey === _rangeKey) {
            predefinedRange = true

            if (!this.startDateCompare || !this.startDateCompare.isSame(range.startDate)) {
              this.startDateCompare = this.$dayjs.utc(range.startDate)
            }
            if (!this.endDateCompare || !this.endDateCompare.isSame(range.endDate)) {
              this.endDateCompare = this.$dayjs.utc(range.endDate)
            }
          }
        }
      }

      // Custom range
      this.rangeSelectCompare = rangeKey
      if (!predefinedRange && rangeKey === RANGE_SELECT.CUSTOM) {
        this.step = STEP.START_DATE_COMPARE;
        this.previousStep = STEP.START_DATE_COMPARE;
        this.startDateCompare = ''
        this.endDateCompare = ''

        const $vm = this
        setTimeout(function() {
          $vm.$refs.startDateCompare && $vm.$refs.startDateCompare.focus()
        }, 0)
      } else if (rangeKey === RANGE_SELECT.LAST_PERIOD) {
        this.startDateCompare = ''
        this.endDateCompare = ''
        //Fix bug change range select compare after save custom range (focus input date)
        this.step = null;
      }
    },
    selectDate: function(date) {
      if (this.step === STEP.SELECT_START_DATE) {
        this.startDate = date
      } else if (this.step === STEP.SELECT_END_DATE) {
        this.endDate = date
      } else if (this.step === STEP.SELECT_START_DATE_COMPARE) {
        this.startDateCompare = date
      } else if (this.step === STEP.SELECT_END_DATE_COMPARE) {
        this.endDateCompare = date
      }
    },

    // Step flow for date range selections
    nextStep: function(day) {
      //In case when user reselect start date for range select
      if (day && this.step === null && this.rangeSelect === RANGE_SELECT.CUSTOM && this.previousStep === STEP.SELECT_END_DATE) {
        this.startDate = day;
        this.step = STEP.SELECT_END_DATE;
        this.$refs.endDate.focus()
        //In case when user reselect start date for range select compare
      } else if (day && this.step === null && this.rangeSelectCompare === RANGE_SELECT.CUSTOM && this.previousStep === STEP.SELECT_END_DATE_COMPARE) {
        this.startDateCompare = day;
        this.step = STEP.SELECT_END_DATE_COMPARE;
        this.$refs.endDateCompare.focus()
      } else if (day && this.step === null) {
        return;
      } else if (this.step === STEP.SELECT_START_DATE) {
        this.step = STEP.SELECT_END_DATE
        this.$refs.endDate.focus()
      } else if (this.step === STEP.SELECT_END_DATE) {
        this.step = null
        this.$refs.endDate.blur()
      } else if (this.step === STEP.SELECT_START_DATE_COMPARE) {
        this.step = STEP.SELECT_END_DATE_COMPARE
        this.$refs.endDateCompare.focus()
      } else if (this.step === STEP.SELECT_END_DATE_COMPARE) {
        this.step = null
        this.$refs.endDateCompare.blur()
      }
      if (this.step !== null) {
        this.$set(this, 'previousStep', this.step);
      }
    },
    // Try to update the step date from an input value
    inputDate: function(input) {
      //This is stricky here, convert utc with hour is 00:00:00 is wrong, it always return previous day
      let date = this.$dayjs.utc(input.target.value, getFullDateFormatByLocalization()).add(-new Date().getTimezoneOffset(), 'minutes'); // add 8 로 하니까 하루씩 밀림
      this.message.single.endDateIsLessThanStartDate = ''
      this.message.compare.endDateIsLessThanStartDate = '';
      if (date.isValid()) {
        this.selectDate(date)
        this.validateCustomTimeFrame()
        this.validateEndDateThanStartDate()
      }
      this.nextStep()
    },
    inputCompareDate(input) {
      //This is stricky here, convert utc with hour is 00:00:00 is wrong, it always return previous day
      let date = this.$dayjs.utc(input.target.value, getFullDateFormatByLocalization()).add(-new Date().getTimezoneOffset(), 'minutes');
      this.message.compare.endDateIsLessThanStartDate = '';
      if (date.isValid()) {
        this.selectDate(date)
        this.validateCustomCompareTimeFrame()
        this.validateEndDateCompareThanStartDateCompare()
      }
      this.nextStep()
    },
    onChangeDate(input) {
      let date = this.$dayjs.utc(input.target.value, getFullDateFormatByLocalization()).add(-new Date().getTimezoneOffset(), 'minutes');
      //TODO (2020.11.06) 릴리즈 이후 다시 복구예정 (날짜제한 제거 필요)
      //TODO (2021.03.16) 이전 날짜 제한 여부에 따른 처리 추라
      if(date - this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month') < 0 && this.isBeforeLimit){
        date = this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month');
      }
      this.selectDate(date)
      this.refreshViewpointMonth()
      this.getStartDateAndEndDateCompareRangeLastPeriod();
    },
    onChangeCompareDate(input) {
      let date = this.$dayjs.utc(input.target.value, getFullDateFormatByLocalization()).add(-new Date().getTimezoneOffset(), 'minutes');
      //TODO (2020.11.06) 릴리즈 이후 다시 복구예정 (날짜제한 제거 필요)
      //TODO (2021.03.16) 이전 날짜 제한 여부에 따른 처리 추라
      if(date - this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5) < 0 && this.isBeforeLimit){
        date = this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month');
      }
      this.selectDate(date)
    },
    // Submit button
    submit: function() {
      if (!this.isSubmitButtonEnabled) {
        return;
      }
      this.$emit('submit', {
        startDate: this.startDate,
        endDate: this.endDate,
        compare: this.compare,
        rangeSelect: this.rangeSelect,
        rangeSelectCompare: this.rangeSelectCompare,
        startDateCompare: this.$dayjs.utc(this.startDateCompare).format('YYYY-MM-DD'),
        endDateCompare: this.$dayjs.utc(this.endDateCompare).format('YYYY-MM-DD'),
        compareCostType: this.compareCostType
      })
    },
    // Cancel button
    cancel: function() {
      this.$emit('cancel')
    },
    isNumber(event) {
      let charCode = (event.which) ? event.which : event.keyCode;
      const charCodeZero = 48
      const chartCodeNine = 57
      const chartCodeSlash = 47
      const chartCodeCondition = 32
      if ((charCode > chartCodeCondition && (charCode < charCodeZero || charCode > chartCodeNine)) && charCode !== chartCodeSlash) {
        event.preventDefault();
      } else {
        return true;
      }
    },
    validateCustomCompareTimeFrame() {
      if (this.checkDatePeriodGreaterThan93Days(this.startDateCompare, this.endDateCompare)) {
        this.getStartDateAndEndDateCompareRangeLastPeriod();
        setTimeout(() => {
          this.$refs.startDateCompare.blur();
          this.$refs.endDateCompare.focus();
          this.message.compare.exceeded93Days = this.$t('costAnalytics.header.exceeded93daysWarningMessage');
        }, 1)
      }
      else {
        if(this.$dayjs(this.startDateCompare) - this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5) < 0 && this.isBeforeLimit){
          this.startDateCompare = this.dateFormat(this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month').add(1, 'day'),'startDate');
        }
        if(this.$dayjs(this.endDateCompare) - this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5) < 0 && this.isBeforeLimit){
          this.endDateCompare = this.dateFormat(this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month').add(1, 'day'),'endDate');
        }
        this.message.compare.exceeded93Days = '';
      }
    },
    validateCustomTimeFrame() {
      if (this.compare) {
        if (this.checkDatePeriodGreaterThan93Days(this.startDate, this.endDate)) {
          this.rangeSelect = RANGE_SELECT.CUSTOM;
          this.startDate = this.$dayjs.utc(this.endDate).subtract(92, 'day').startOf('day');
          this.getStartDateAndEndDateCompareRangeLastPeriod();
          setTimeout(() => {
            this.$refs.startDate.blur();
            this.$refs.endDate.focus();
            this.message.single.exceeded93Days = this.$t('costAnalytics.header.exceeded93daysWarningMessage');
          }, 100)
        } else {
          if(this.compare){
            if(this.$dayjs(this.startDateCompare) - this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5) < 0 && this.isBeforeLimit){
              this.startDateCompare = this.dateFormat(this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month').add(1, 'day'),'startDate');
            }
            if(this.$dayjs(this.endDateCompare) - this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5) < 0 && this.isBeforeLimit){
              this.endDateCompare = this.dateFormat(this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month').add(1, 'day'),'endDate');
            }
          }
          this.message.single.exceeded93Days = '';
        }
      } else {
        if (this.checkDatePeriodGreaterThanOneYear(this.startDate, this.endDate)) {
          this.message.single.exceeded12Months = this.$t('costAnalytics.header.exceeded12MonthsWarningMessage');
          setTimeout(() => {
            this.$refs.startDate.blur()
            this.$refs.endDate.blur()
          }, 1)
        } else {
          if(this.compare){
            if(this.$dayjs(this.startDateCompare) - this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5) < 0 && this.isBeforeLimit){
              this.startDateCompare = this.dateFormat(this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month'),'startDate');
            }
            if(this.$dayjs(this.endDateCompare) - this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5) < 0 && this.isBeforeLimit){
              this.endDateCompare = this.dateFormat(this.$dayjs().utc().set('month', this.$dayjs().utc().month() - 5).startOf('month'),'endDate');
            }
          }
          this.message.single.exceeded12Months = '';
        }
      }
    },
    validateSelectCalendar() {
      if (this.checkDatePeriodGreaterThanOneYear(this.startDate, this.endDate) && this.step === STEP.SELECT_END_DATE) {
        this.$refs.endDate.blur()
        this.step = STEP.SELECT_END_DATE
        this.$refs.endDate.focus()
      }
    },
    validateSelectCompareCalendar() {
      if (this.checkDatePeriodGreaterThanOneYear(this.startDateCompare, this.endDateCompare) && this.step === STEP.SELECT_END_DATE_COMPARE) {
        this.$refs.endDateCompare.blur();
        this.step = STEP.SELECT_END_DATE_COMPARE;
        this.$refs.endDateCompare.focus();
      }
    },
    checkDatePeriodGreaterThanOneYear(startDate, endDate) {
      const newStartDate = new Date(startDate.year(), startDate.month(), startDate.date());
      const previousEndDate = new Date(endDate.year() - 1, endDate.month(), endDate.date());
      return newStartDate < previousEndDate;
    },
    checkDatePeriodGreaterThan93Days(startDate, endDate) {
      return endDate.diff(startDate, 'day') > 93
    },
    validateEndDateCompareThanStartDateCompare() {
      const newStartDateCompare = new Date(this.startDateCompare.year(), this.startDateCompare.month(), this.startDateCompare.date());
      const newEndDateCompare = new Date(this.endDateCompare.year(), this.endDateCompare.month(), this.endDateCompare.date());

      if (newEndDateCompare < newStartDateCompare) {
        setTimeout(() => {
          this.startDateCompare = this.endDateCompare;
          this.step = STEP.SELECT_END_DATE_COMPARE;
          this.$refs.endDateCompare.focus();
        }, 2)
        if (this.step === STEP.SELECT_START_DATE_COMPARE || this.step === STEP.SELECT_END_DATE_COMPARE) {
          setTimeout(() => {
            this.message.compare.endDateIsLessThanStartDate = this.$t('costAnalytics.header.endDateIsLessThanStartDateWarningMessage');
            this.endDateCompare = this.startDateCompare;
            this.step = STEP.SELECT_END_DATE_COMPARE;
            this.$refs.endDateCompare.focus();
          }, 1)
        }
      }
    },
    validateEndDateThanStartDate() {
      const newStartDate = new Date(this.startDate.year(), this.startDate.month(), this.startDate.date());
      const newEndDate = new Date(this.endDate.year(), this.endDate.month(), this.endDate.date());

      if (newEndDate < newStartDate) {
        setTimeout(() => {
          this.startDate = this.endDate
          this.step = STEP.SELECT_END_DATE
          this.$refs.endDate.focus()
        }, 2)
        if (this.step === STEP.SELECT_START_DATE || this.step === STEP.SELECT_END_DATE) {
          setTimeout(() => {
            this.message.single.endDateIsLessThanStartDate = this.$t('costAnalytics.header.endDateIsLessThanStartDateWarningMessage')
            this.endDate = this.startDate
            this.step = STEP.SELECT_END_DATE
            this.$refs.endDate.focus()
          }, 1)
        }
      }
    }
  },
}
</script>

<style lang="scss">
  /* Custom row */
  .date-range-picker-row {
    border-bottom: 1px solid #D5DAE0;
  }

  .date-range-picker-calendar-col {
    width: 220px;
    padding: 0 10px 8px 10px;
    flex-basis: 100%;
  }

  .date-range-picker-col {
    max-width: 220px;
    margin-right: 10px;
    flex-basis: 100%;
    //There is stricky here, so we have to add min-width: 0 to fix bug Firefox doesn't apply flex-basis if children have larger image
    //Please take a look this link to know more about issue https://github.com/philipwalton/flexbugs/issues/41
    min-width: 0;
    .custom-select-time-frame {
      .custom-select {
        width: 220px;
        &.compare {
          color: #BCC2C8;
        }
      }
    }
    .margin-dash {
      margin-left: 6px;
      margin-right: 5px;
    }
    .input-compare-date-time {
      width: 220px;
      input {
        border-radius: 4px;
      }
    }
    .input-date-time {
      width: 220px;
      input {
        border-radius: 4px;
      }
    }
    .invalid-date-border {
      border-color: red !important;
    }
    .invalid-date-text {
      color: red !important;
    }
  }
  .set-range-select-title {
    margin-bottom: 8px;
    margin-top: 11px;
  }

  /* Make sure that the full date (YYYY-MM-DD) is displayed */
  .date-range-picker-date-input {
    min-width: 102px;
    &:disabled {
      background-color: #f2f4f6;
      color: #b0b7bf;
    }
    &:focus {
      box-shadow: none !important;
    }
    &.compare {
      &:focus {
        box-shadow: none !important;
      }
    }
  }

  /* Select menus border */
  .date-range-picker-range-border {
    &:focus {
      box-shadow: none !important;
      border-color: #D5DAE0 !important;
    }
    &.compare {
      &:focus {
        box-shadow: none !important;
        border-color: #D5DAE0 !important;
      }
    }
  }

  .custom-btn-group {
    position: absolute;
    right: 0;
    bottom: 0;
  }

  .warning-modal {

    .modal-dialog {
      width: 400px;
      .modal-header {
        text-align: center;
        .modal-title {
          position: absolute;
          left: 42%;
          font-size: 16px;
        }
      }
      .modal-body {
        padding: 0 !important;
        .warning-popup-header {
          font-weight: bold;
          font-size: 14px;
          text-align: center;
          border-bottom: 0.5px solid #D5DAE0;
          padding: 10px 0;
        }
        .warning-popup-content {
          padding: 20px;
          border-bottom: 0.5px solid #D5DAE0;
        }
        .warning-popup-footer {
          .btn-action {
            float: right;
            margin: 20px;
            background-color: #0672FF;
            border-radius: 4px;
            border-color: #0672FF !important;
            &:focus {
              box-shadow: none !important;
              background-color: #0672FF !important;
            }
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/common/date-range-picker/DateRangePicker.vue